/**
 * Chunk load error handler for Next.js App Router
 * Injected with `beforeInteractive`
 *
 * Strategy:
 *  Attempt 1 – hard reload with cache-bust (handles transient network hiccup)
 *  Attempt 2 – navigate away (home or history.back) to escape stale/broken route
 *  Exhausted – show a persistent non-blocking banner and log the error
 *
 * All user-facing prompts are rendered as injected banners — never confirm().
 */

function initChunkErrorHandler() {
  if (typeof window === 'undefined') return

  // ---------------------------------------------------------------------------
  // Config
  // ---------------------------------------------------------------------------

  const RETRY_STORAGE_KEY = 'chunk-error-retry'
  const MAX_RETRIES = 2 // attempt 1 = reload, attempt 2 = navigate away
  const RETRY_RESET_DELAY_MS = 30_000
  const DEBOUNCE_MS = 1_000

  // ---------------------------------------------------------------------------
  // i18n
  // ---------------------------------------------------------------------------

  const browserLocale = (navigator.language || 'en').split('-')[0]

  function t(key) {
    const map = {
      reload_prompt: {
        en: 'A loading error has occurred. Please check your connection.',
        fr: 'Une erreur de chargement est survenue. Veuillez vérifier votre connexion.',
      },
      reload_page_btn: { en: 'Reload page', fr: 'Recharger la page' },
      navigate_prompt: {
        en: "We still can't load the page. Please check your connection or contact support.",
        fr: "Nous n'arrivons toujours pas à charger la page. Veuillez vérifier votre connexion ou contactez le support.",
      },
      navigate_back_btn: {
        en: 'Go to previous page',
        fr: 'Revenir à la page précédente',
      },
      navigate_home_btn: {
        en: 'Go to home page',
        fr: "Revenir à la page d'accueil",
      },
      unrecoverable: {
        fr: "Cette page n'a pas pu être chargée pour le moment. Veuillez réessayer plus tard ou contactez le support.",
        en: 'This page failed to load and could not be loaded. Please try again later or contact support.',
      },
      dismiss_btn: { en: 'Dismiss', fr: 'Ignorer' },
    }[key]

    if (!map) {
      return key
    }

    return map[browserLocale] ?? map['en']
  }

  // ---------------------------------------------------------------------------
  // Retry state (sessionStorage)
  // Shape: { url: string, count: number, lastAttemptAt: number }
  //
  // We normalize the URL by stripping the `_cb` cache-bust param so that
  // a cacheBustReload() doesn't produce a new URL that fails the match check
  // and silently resets count back to 0.
  // ---------------------------------------------------------------------------

  function normalizeUrl() {
    const url = new URL(location.href)
    url.searchParams.delete('_cb')

    return url.pathname + url.search + url.hash
  }

  function freshState() {
    return { url: normalizeUrl(), count: 0, lastAttemptAt: 0 }
  }

  function readRetryState() {
    try {
      const raw = sessionStorage.getItem(RETRY_STORAGE_KEY)
      if (!raw) return freshState()
      const parsed = JSON.parse(raw)
      if (!parsed || parsed.url !== normalizeUrl()) return freshState()
      if (Date.now() - parsed.lastAttemptAt > RETRY_RESET_DELAY_MS) return freshState()

      return parsed
    } catch {
      return freshState()
    }
  }

  function writeRetryState(count) {
    try {
      sessionStorage.setItem(
        RETRY_STORAGE_KEY,
        JSON.stringify({ url: normalizeUrl(), count, lastAttemptAt: Date.now() }),
      )
    } catch {
      /* private navigation mode */
    }
  }

  // ---------------------------------------------------------------------------
  // Non-blocking banner UI
  // ---------------------------------------------------------------------------

  const BANNER_ID = 'chunk-error-banner'

  function dismissBanner() {
    document.getElementById(BANNER_ID)?.remove()
  }

  function isBannerVisible() {
    return document.getElementById(BANNER_ID) !== null
  }

  /**
   * Shows an inline banner instead of a blocking confirm() dialog.
   * @param {string} message   Main text
   * @param {string} confirmLabel   Text for the confirm CTA button (required)
   * @param {() => void} [onConfirm]  Called when the user clicks the CTA
   * @param {{ noDismiss?: boolean }} [options]  Optional settings
   *    - noDismiss: if true, the banner won't render a dismiss button and can't be dismissed by the user
   *                 (useful for unrecoverable state where we want to force the user to refresh or navigate away)
   * @returns {void}
   */
  function showBanner(message, confirmLabel, onConfirm, options = {}) {
    dismissBanner()

    const displayConfirm = typeof onConfirm === 'function' && typeof confirmLabel === 'string'

    const theme = {
      banner: 'bg-mango-orange-50 border-mango-orange-300 text-mango-orange-900',
      confirm: 'bg-mango-orange-400 hover:bg-mango-orange-500 text-white border-none',
      dismiss:
        'bg-clear border border-mango-orange-300 text-mango-orange-900 hover:bg-mango-orange-100',
    }
    const tw = theme

    const banner = document.createElement('div')
    banner.id = BANNER_ID
    banner.className = [
      'fixed w-full top-2 left-1/2 -translate-x-1/2 z-[99999]',
      'flex gap-1 tablet:gap-2 flex-wrap',
      'p-1 pl-2 rounded-sm border-[1.5px]',
      'text-md max-w-[calc(100vw-2rem)] tablet:max-w-[30rem]',
      'shadow-[0_0.25rem_0.75rem_rgba(0,0,0,0.12)]',
      tw.banner,
      displayConfirm ? 'justify-end' : 'items-center justify-end',
    ]
      .filter(Boolean)
      .join(' ')

    const msg = document.createElement('p')

    if (displayConfirm || !options.noDismiss) {
      msg.className = 'pt-1 '
    }

    if (displayConfirm) {
      msg.className += 'w-full'
    } else {
      msg.className += 'flex-1 text-left'
    }

    msg.textContent = message
    banner.appendChild(msg)

    // Shared button base classes
    const btnBase =
      'px-2 py-0.5 rounded-xs text-md font-bold whitespace-nowrap cursor-pointer transition-colors'

    if (!options.noDismiss) {
      const dismissBtn = document.createElement('button')
      dismissBtn.textContent = t('dismiss_btn')
      dismissBtn.className = `${btnBase} ${tw.dismiss}`
      dismissBtn.addEventListener('click', dismissBanner)
      banner.appendChild(dismissBtn)
    }

    if (onConfirm && confirmLabel) {
      const confirmBtn = document.createElement('button')
      confirmBtn.textContent = t(confirmLabel)
      confirmBtn.className = `${btnBase} ${tw.confirm}`
      confirmBtn.addEventListener('click', () => {
        dismissBanner()
        onConfirm()
      })
      banner.appendChild(confirmBtn)
    }

    document.body?.appendChild(banner)
  }

  // ---------------------------------------------------------------------------
  // Error logging
  // ---------------------------------------------------------------------------

  async function logChunkError(errorMessage) {
    try {
      await fetch('/api/log-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ error: `Chunk load error: ${errorMessage}` }),
      })
    } catch (err) {
      console.error('[chunk-error] Failed to log error', err)
    }
  }

  // ---------------------------------------------------------------------------
  // Reload helpers
  // ---------------------------------------------------------------------------

  /** Hard reload with a cache-busting query param to avoid re-fetching stale chunks. */
  function cacheBustReload() {
    const url = new URL(location.href)
    url.searchParams.set('_cb', Date.now())
    location.replace(url.toString())
  }

  // ---------------------------------------------------------------------------
  // Core handler  (debounced — replaces the isHandling flag)
  // ---------------------------------------------------------------------------

  function debounce(fn, ms) {
    let timer

    return function (...args) {
      clearTimeout(timer)
      timer = setTimeout(() => {
        try {
          fn.apply(this, args)
        } catch (err) {
          console.error('[chunk-error] Error in debounced function', err)
        }
      }, ms)
    }
  }

  function showUnrecoverableBanner() {
    showBanner(t('unrecoverable'), undefined, undefined, { noDismiss: true })
  }

  function getNetworkQuality() {
    let networkQuality = null

    try {
      if (navigator.connection && navigator.connection.effectiveType) {
        networkQuality = navigator.connection.effectiveType
      } else {
        networkQuality = navigator.onLine ? 'unknown' : 'was-offline'
      }
    } catch {
      networkQuality = 'unavailable'
    }

    return { networkQuality }
  }

  function logWarningWithNetworkInfo(message) {
    const { networkQuality } = getNetworkQuality()
    console.warn('[chunk-error]', message, { networkQuality })
  }

  const handleChunkError = debounce(async function (errorMessage) {
    const state = readRetryState()

    if (state.count >= MAX_RETRIES) {
      // Guard: if the unrecoverable banner is already visible, the error is
      // still firing on a broken page — don't re-log or re-render on every event.
      if (isBannerVisible()) return

      await logChunkError(errorMessage)
      showUnrecoverableBanner()

      return
    }

    writeRetryState(state.count + 1)

    if (state.count === 0) {
      // ── Attempt 1: hard reload with cache-bust ──────────────────────────────
      logWarningWithNetworkInfo('User opted to retry loading the page (attempt 1)')
      showBanner(t('reload_prompt'), 'reload_page_btn', () => {
        setTimeout(cacheBustReload, 0)
      })
    } else {
      // ── Attempt 2: navigate away to escape broken route ─────────────────────
      if (history.length > 1) {
        logWarningWithNetworkInfo('No retries left, offering user to navigate back in history')
        showBanner(t('navigate_prompt'), 'navigate_back_btn', () => {
          history.back()
        })
      } else if (location.pathname !== '/') {
        logWarningWithNetworkInfo('No history to go back to, navigating to home page')
        showBanner(t('navigate_prompt'), 'navigate_home_btn', () => {
          location.href = '/'
        })
      } else {
        logWarningWithNetworkInfo('No navigation options available, showing unrecoverable banner')
        showUnrecoverableBanner()
      }
    }
  }, DEBOUNCE_MS)

  // ---------------------------------------------------------------------------
  // Global listeners
  // ---------------------------------------------------------------------------

  window.addEventListener(
    'error',
    (event) => {
      try {
        const target = event.target

        // Script tag failed to load
        if (target instanceof HTMLScriptElement) {
          const { src } = target
          if (!src) return

          if (src.includes('_next/static/chunks/') || src.includes('.next/static/chunks/')) {
            logWarningWithNetworkInfo(`Script load failed: ${src}`)
            handleChunkError(`Failed to load script: ${src}`)
          }

          return
        }

        // ChunkLoadError thrown as a generic ErrorEvent
        if (event instanceof ErrorEvent) {
          if (event.message?.toLowerCase().includes('chunkloaderror')) {
            logWarningWithNetworkInfo(`ChunkLoadError in ErrorEvent: ${event.message}`)
            handleChunkError(`ChunkLoadError: ${event.message}`)
          }
        }
      } catch (err) {
        console.error('[chunk-error] Error in error event handler', err)
      }
    },
    true, // capture phase — catches errors before they're swallowed
  )

  window.addEventListener('unhandledrejection', (event) => {
    try {
      const reason = event.reason
      if (!(reason instanceof Error)) return

      const msg = reason.message.toLowerCase()

      if (
        msg.includes('chunkloaderror') ||
        msg.includes('loading chunk') ||
        msg.includes('dynamically imported module')
      ) {
        logWarningWithNetworkInfo(`Unhandled rejection with chunk error: ${reason.message}`)
        handleChunkError(reason.message)
      }
    } catch (err) {
      console.error('[chunk-error] Error in unhandledrejection handler', err)
    }
  })

  // Manual hook for debugging in the console
  window.__handleChunkError = handleChunkError
}

initChunkErrorHandler()
